import { Controller } from "@hotwired/stimulus"

// The territories + platforms step, shared by the album and music video
// wizards.
//
// Element ids and copy come in as values so a second wizard can reuse this
// controller without renaming its fields. The defaults are the album page's,
// so that page keeps working untouched.
export default class extends Controller {
  static targets = ["form", "submitButton", "submitButtonSpinner", "submitButtonText"]
  static values = {
    includedFieldId: { type: String, default: "album_included_territories" },
    excludedFieldId: { type: String, default: "album_excluded_territories" },
    dspFieldId: { type: String, default: "album_selected_dsps" },
    territoryError: { type: String, default: "At least one territory must be included or excluded" },
    dspError: { type: String, default: "At least one DSP must be selected" },
    savingText: { type: String, default: "Saving..." },
    submitText: { type: String, default: "SAVE AND CONTINUE \u2192" }
  }

  connect() {
    this.formTarget.addEventListener("submit", this.validateForm.bind(this))
  }

  validateForm(event) {
    const errors = this.collectValidationErrors()

    if (errors.length > 0) {
      event.preventDefault()
      this.showValidationErrors(errors)
      return false
    }

    // Show loading state when validation passes
    this.setSubmitButtonLoading(true)

    return true
  }

  collectValidationErrors() {
    const errors = []

    // Check if at least one territory is included or excluded
    const includedTerritories = document.getElementById(this.includedFieldIdValue)
    const excludedTerritories = document.getElementById(this.excludedFieldIdValue)

    let hasIncludedTerritories = false
    let hasExcludedTerritories = false

    if (includedTerritories && includedTerritories.value) {
      try {
        const included = JSON.parse(includedTerritories.value)
        hasIncludedTerritories = Array.isArray(included) && included.length > 0
      } catch (e) {
        hasIncludedTerritories = false
      }
    }

    if (excludedTerritories && excludedTerritories.value) {
      try {
        const excluded = JSON.parse(excludedTerritories.value)
        hasExcludedTerritories = Array.isArray(excluded) && excluded.length > 0
      } catch (e) {
        hasExcludedTerritories = false
      }
    }

    if (!hasIncludedTerritories && !hasExcludedTerritories) {
      errors.push(this.territoryErrorValue)
    }

    // Check if at least one DSP is selected
    const selectedDsps = document.getElementById(this.dspFieldIdValue)
    if (selectedDsps && selectedDsps.value) {
      try {
        const dsps = JSON.parse(selectedDsps.value)
        if (!Array.isArray(dsps) || dsps.length === 0) {
          errors.push(this.dspErrorValue)
        }
      } catch (e) {
        errors.push(this.dspErrorValue)
      }
    } else {
      errors.push(this.dspErrorValue)
    }

    return errors
  }

  showValidationErrors(errors) {
    // Remove any existing error display
    this.removeExistingErrors()

    // Create error summary
    const errorContainer = document.createElement('div')
    errorContainer.className = 'rounded-md bg-red-50 p-4 mb-6'
    errorContainer.id = 'client-validation-errors'

    const errorContent = `
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">
            There ${errors.length === 1 ? 'is' : 'are'} ${errors.length} error${errors.length === 1 ? '' : 's'} with your submission
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <ul class="list-disc pl-5 space-y-1">
              ${errors.map(error => `<li>${error}</li>`).join('')}
            </ul>
          </div>
        </div>
      </div>
    `

    errorContainer.innerHTML = errorContent

    // Insert error container at the top of the form
    this.formTarget.insertBefore(errorContainer, this.formTarget.firstChild)

    // Scroll to the top of the form to show errors
    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Highlight sections with red borders
    this.highlightErrorSections()
  }

  removeExistingErrors() {
    const existingErrors = document.getElementById('client-validation-errors')
    if (existingErrors) {
      existingErrors.remove()
    }

    // Remove red border from territory sections
    const includedSection = document.querySelector('[data-territory-section="included"]')
    if (includedSection) {
      includedSection.classList.remove('border-red-500')
      includedSection.classList.add('border-gray-300')
    }

    const excludedSection = document.querySelector('[data-territory-section="excluded"]')
    if (excludedSection) {
      excludedSection.classList.remove('border-red-500')
      excludedSection.classList.add('border-gray-300')
    }

    // Remove red border from DSP section
    const dspSection = document.querySelector('[data-dsp-section]')
    if (dspSection) {
      dspSection.classList.remove('border-red-500')
      dspSection.classList.add('border-gray-300')
    }
  }

  highlightErrorSections() {
    // Highlight territory sections if both are empty
    const includedTerritories = document.getElementById(this.includedFieldIdValue)
    const excludedTerritories = document.getElementById(this.excludedFieldIdValue)

    let hasIncludedTerritories = false
    let hasExcludedTerritories = false

    if (includedTerritories && includedTerritories.value) {
      try {
        const included = JSON.parse(includedTerritories.value)
        hasIncludedTerritories = Array.isArray(included) && included.length > 0
      } catch (e) {
        hasIncludedTerritories = false
      }
    }

    if (excludedTerritories && excludedTerritories.value) {
      try {
        const excluded = JSON.parse(excludedTerritories.value)
        hasExcludedTerritories = Array.isArray(excluded) && excluded.length > 0
      } catch (e) {
        hasExcludedTerritories = false
      }
    }

    if (!hasIncludedTerritories && !hasExcludedTerritories) {
      const includedSection = document.querySelector('[data-territory-section="included"]')
      if (includedSection) {
        includedSection.classList.remove('border-gray-300')
        includedSection.classList.add('border-red-500')
      }

      const excludedSection = document.querySelector('[data-territory-section="excluded"]')
      if (excludedSection) {
        excludedSection.classList.remove('border-gray-300')
        excludedSection.classList.add('border-red-500')
      }
    }

    // Highlight DSP section if no DSPs selected
    const selectedDsps = document.getElementById(this.dspFieldIdValue)
    const dspSection = document.querySelector('[data-dsp-section]')

    if (dspSection && selectedDsps) {
      try {
        const dsps = JSON.parse(selectedDsps.value)
        if (!Array.isArray(dsps) || dsps.length === 0) {
          dspSection.classList.remove('border-gray-300')
          dspSection.classList.add('border-red-500')
        }
      } catch (e) {
        dspSection.classList.remove('border-gray-300')
        dspSection.classList.add('border-red-500')
      }
    }
  }

  setSubmitButtonLoading(loading) {
    if (!this.hasSubmitButtonTarget) return

    if (loading) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('opacity-75', 'cursor-not-allowed')
      this.submitButtonTarget.classList.remove('hover:bg-green-700', 'cursor-pointer')

      if (this.hasSubmitButtonSpinnerTarget) {
        this.submitButtonSpinnerTarget.classList.remove('hidden')
      }

      if (this.hasSubmitButtonTextTarget) {
        this.submitButtonTextTarget.textContent = this.savingTextValue
      }
    } else {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('opacity-75', 'cursor-not-allowed')
      this.submitButtonTarget.classList.add('hover:bg-green-700', 'cursor-pointer')

      if (this.hasSubmitButtonSpinnerTarget) {
        this.submitButtonSpinnerTarget.classList.add('hidden')
      }

      if (this.hasSubmitButtonTextTarget) {
        this.submitButtonTextTarget.textContent = this.submitTextValue
      }
    }
  }
}
